'use client';

import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import { AppBar, Box, Drawer, IconButton, Stack, Typography } from '@mui/material';
import Image from 'next/image';
import Link from 'next/link';
import React, { cloneElement, useCallback, useEffect, useState } from 'react';

import HexagonalButton from '@/components/bases/HexagonalButton';
import { useGoogleCalendar } from '@/providers/GoogleCalendarProvider';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const { showModal } = useGoogleCalendar();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);

    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  const navItems = [
    { label: 'CORE SERVICES', sectionId: 'core-services' },
    { label: 'WHY ORBIT?', sectionId: 'why-orbit' },
  ];

  const handleSmoothScroll = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80; // Offset for fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      setTimeout(() => {
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }, 100);
    }
  }, []);

  const renderNav = useCallback(
    (onMobileMenuClose?: () => void) => {
      return navItems.map(nav => (
        <Typography
          key={nav.sectionId}
          onClick={() => {
            handleSmoothScroll(nav.sectionId);
            onMobileMenuClose?.();
          }}
          sx={{
            color: '#FFFFFF80',
            fontWeight: 400,
            cursor: 'pointer',
            '&:hover': { color: '#FFFFFF' },
            transition: 'color 0.2s ease',
          }}>
          {nav.label}
        </Typography>
      ));
    },
    [handleSmoothScroll],
  );

  return (
    <AppBar
      position='fixed'
      sx={{
        backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.01)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        boxShadow: 'none',
        transition: 'all 0.3s ease-in-out',
        py: 1,
        zIndex: theme => theme.zIndex.drawer + 1,
      }}>
      <Stack width={'100%'} alignItems={'center'} paddingX={{ xs: '24px', sm: '80px' }}>
        <Stack
          width={'min(1280px, 100%)'}
          direction={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
          paddingY={1}>
          <Stack alignItems={'center'} justifyContent={'center'}>
            <Link
              href='https://lfg.inc'
              target={'_blank'}
              style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              <Box sx={{ position: 'relative', width: 50, height: 50 }}>
                <Image src='/logo.png' alt='LFG Logo' fill style={{ objectFit: 'contain' }} priority />
              </Box>
            </Link>
          </Stack>

          <Stack direction={'row'} gap={4} alignItems={'center'}>
            {/* Desktop Navigation */}
            <Stack gap={4} direction={'row'} alignItems={'center'} display={{ xs: 'none', md: 'flex' }}>
              {renderNav()}
            </Stack>

            {/* Desktop Contact Button */}
            <Stack display={{ xs: 'none', md: 'flex' }} width={'140px'}>
              <HexagonalButton
                variant='primary'
                label='CONTACT US'
                // height={40}
                labelClassName='!text-[24px]'
                onClick={showModal}
                zoom={0.8}
              />
            </Stack>

            {/* Mobile Menu */}
            <MobileMenu onSmoothScroll={handleSmoothScroll}>
              <Stack gap={3} padding={3}>
                {renderNav()}
                <Stack width={'160px'} alignSelf={'center'} marginTop={2}>
                  <HexagonalButton
                    variant='primary'
                    label='CONTACT US'
                    // height={40}
                    labelClassName='!text-[24px]'
                    onClick={showModal}
                    zoom={0.8}
                  />
                </Stack>
              </Stack>
            </MobileMenu>
          </Stack>
        </Stack>
      </Stack>
    </AppBar>
  );
};

interface MobileMenuProps {
  children: React.ReactElement<{ onClick?: () => void }>;
  onSmoothScroll: (sectionId: string) => void;
}

const MobileMenu = ({ children, onSmoothScroll }: MobileMenuProps) => {
  const [initialPadding, setInitialPadding] = useState<string>('');
  const [isDrawerOpened, setIsDrawerOpened] = useState<boolean>(false);

  const toggle = () => setIsDrawerOpened(prev => !prev);
  const handleClose = () => setIsDrawerOpened(false);

  useEffect(() => {
    if (isDrawerOpened) {
      setInitialPadding(document.body.style.paddingRight);
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;

      document.body.style.paddingRight = `${scrollBarWidth}px`;

      document.body.style.overflow = 'hidden';
      document.body.style.height = '100%';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';

      return () => {
        document.body.style.overflow = '';
        document.body.style.height = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.paddingRight = initialPadding;
      };
    }
  }, [isDrawerOpened, initialPadding]);

  return (
    <Stack sx={{ display: { xs: 'contents', md: 'none' } }}>
      <IconButton onClick={toggle} sx={{ padding: 0 }}>
        {isDrawerOpened ? (
          <CloseIcon fontSize={'medium'} sx={{ color: '#FFF' }} />
        ) : (
          <MenuIcon fontSize={'medium'} sx={{ color: '#FFF' }} />
        )}
      </IconButton>

      <Drawer
        anchor={'top'}
        hideBackdrop
        variant={'temporary'}
        open={isDrawerOpened}
        onClose={() => setIsDrawerOpened(false)}
        sx={{ zIndex: 100 }}
        PaperProps={{
          sx: {
            height: '100dvh',
            paddingTop: '72px',
            backgroundColor: '#060912',
            boxShadow: 'none !important',
          },
        }}>
        {cloneElement(children, {
          onClick: handleClose,
        })}
      </Drawer>
    </Stack>
  );
};

export default Header;
